import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Spinner, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import { AgentAssessment, disqualificationRuleReason, nonAiDisqualificationReason, ScrubbingReasoningSummary, specificReasonDetail } from '../../../models';
import { classifyLoadError, ReasoningLoadError, reasoningLoadErrorMessage } from './reasoning-load-error';

interface DisqualificationReasoningDialogProps {
  isOpen: boolean;
  nominationSummary?: ScrubbingReasoningSummary;
  loadAssessments?: (nominationId: number) => Promise<AgentAssessment[]>;
  onOverride(): void;
  onClose(): void;
}

interface DisqualificationReason {
  reason?: string;
  rationale?: string;
}

const useStyles = makeStyles({
  modalSurface: {
    maxWidth: '480px',
    width: '100%',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

const parseLlmReason = (perQuestionScoresJson?: string): DisqualificationReason | null => {
  if (!perQuestionScoresJson) return null;
  try {
    const parsed = JSON.parse(perQuestionScoresJson) as DisqualificationReason;
    return { reason: parsed.reason, rationale: parsed.rationale };
  } catch {
    return null;
  }
};

const DisqualificationReasoningDialogImpl: React.FC<DisqualificationReasoningDialogProps> = (props) => {
  const {
    isOpen,
    nominationSummary,
    loadAssessments,
    onOverride,
    onClose,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [llmReason, setLlmReason] = useState<DisqualificationReason | null>(null);
  const [error, setError] = useState<ReasoningLoadError | null>(null);

  const sourceCode = nominationSummary?.scrubbingSourceCode;

  useEffect(() => {
    if (!isOpen || !nominationSummary || sourceCode !== 'LLM' || !loadAssessments) {
      setLlmReason(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const loadAssessment = async (): Promise<void> => {
      setIsLoading(true);
      setLlmReason(null);
      setError(null);
      try {
        const assessments = await loadAssessments(nominationSummary.nominationId);
        const latestDisqualification = assessments
          .filter((assessment) => assessment.agentType === 1 && assessment.status === 'Completed')
          .sort((a, b) => b.createdDate.localeCompare(a.createdDate))[0];
        if (!cancelled) {
          setLlmReason(parseLlmReason(latestDisqualification?.perQuestionScoresJson));
        }
      } catch (err) {
        if (!cancelled) setError(classifyLoadError(err));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadAssessment();
    return () => {
      cancelled = true;
    };
  }, [isOpen, nominationSummary, sourceCode, loadAssessments]);

  const renderReasonBody = (): React.JSX.Element => {
    if (sourceCode === 'Rule') {
      const ruleReason = disqualificationRuleReason(nominationSummary?.ruleCode);
      const detail = specificReasonDetail(nominationSummary?.scrubbingFeedback) ?? ruleReason.detail;
      return (
        <>
          <Text weight='semibold' block>{ruleReason.label}</Text>
          <Text as='p' block>{detail}</Text>
        </>
      );
    }

    if (isLoading) {
      return <Spinner label='Loading reasoning...' />;
    }

    if (error) {
      return <Text as='p' block>{reasoningLoadErrorMessage(error)}</Text>;
    }

    if (llmReason && (llmReason.reason || llmReason.rationale)) {
      return (
        <>
          {llmReason.reason && <Text weight='semibold' block>{llmReason.reason}</Text>}
          {llmReason.rationale && <Text as='p' block>{llmReason.rationale}</Text>}
        </>
      );
    }

    const nonAiReason = nonAiDisqualificationReason(sourceCode, nominationSummary?.scrubbingFeedback);
    if (nonAiReason) {
      return <Text as='p' block>{nonAiReason.detail}</Text>;
    }

    return <Text as='p' block>No AI disqualification rationale is available for this nomination.</Text>;
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, styles.modalSurface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                onClick={() => onClose()}
              />
            )}
          >
            Disqualification reasoning
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            {nominationSummary && (
              <Text className='text--blue' weight='semibold' block>
                {nominationSummary.title}
              </Text>
            )}
            {renderReasonBody()}
          </DialogContent>
          <DialogActions>
            <Button appearance='secondary' onClick={() => onClose()}>
              Close
            </Button>
            <Button appearance='primary' onClick={() => onOverride()}>
              Override
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const DisqualificationReasoningDialog = observer(DisqualificationReasoningDialogImpl);
